import { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { FolderCog } from 'lucide-react';
import FullModal from './FullModal';
import { InputTextField } from '../Input/InputTextField';

const defaultFormValues = {
  host: '',
  username: '',
  password: '',
  keyfileName: '',
  remoteDirectory: '',
  archiveDirectory: '',
  filenamePattern: '',
  frequency: '',
};

const requiredFields = [
  'host',
  'username',
  'password',
  'keyfileName',
  'remoteDirectory',
  'archiveDirectory',
  'filenamePattern',
  'frequency',
];

const ConfigureDataFeedModal = ({ onClose, onSave, initialValues = null, isEdit = false }) => {
  const [formValues, setFormValues] = useState(() => ({
    ...defaultFormValues,
    ...(initialValues || {}),
    keyfileName: initialValues?.keyfileName || initialValues?.keyfile || '',
  }));
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    setFormValues({
      ...defaultFormValues,
      ...(initialValues || {}),
      keyfileName: initialValues?.keyfileName || initialValues?.keyfile || '',
    });
    setFieldErrors({});
  }, [initialValues]);

  const handleUpdate = (field) => (value) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      [field]: value,
    }));

    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      [field]: value.trim() === '',
    }));
  };

  const handleKeyfileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    const fileName = selectedFile ? selectedFile.name : '';

    setFormValues((currentValues) => ({
      ...currentValues,
      keyfileName: fileName,
    }));

    setFieldErrors((currentErrors) => ({
      ...currentErrors,
      keyfileName: fileName.trim() === '',
    }));
  };

  const handleSave = () => {
    const nextErrors = requiredFields.reduce((errors, field) => {
      errors[field] = String(formValues[field] || '').trim() === '';
      return errors;
    }, {});

    setFieldErrors(nextErrors);

    const hasErrors = Object.values(nextErrors).some(Boolean);
    if (hasErrors) {
      return;
    }

    onSave?.(formValues);
    onClose?.();
  };

  const getInputStyle = (field) => (fieldErrors[field] ? { border: '1px solid #ef4444' } : {});

  return (
    <FullModal onClose={onClose}>
      <div className="configure-data-feed-modal">
        <div className="configure-data-feed-modal__header">
          <FolderCog className="configure-data-feed-modal__icon" size={24} />
          <div>
            <h3 className="configure-data-feed-modal__title">{isEdit ? 'Edit FTP/SFTP' : 'Configure FTP/SFTP'}</h3>
          </div>
        </div>

        <InputTextField
          title="Host / Destination / Source"
          onChange={handleUpdate('host')}
          placeholder="Enter host, destination, or source"
          value={formValues.host}
          style={getInputStyle('host')}
        />
        <InputTextField
          title="Username"
          onChange={handleUpdate('username')}
          placeholder="Enter username"
          value={formValues.username}
          style={getInputStyle('username')}
        />
        <InputTextField
          title="Password"
          type="password"
          onChange={handleUpdate('password')}
          placeholder="Enter password"
          value={formValues.password}
          style={getInputStyle('password')}
        />
        <div className={`configure-data-feed-modal__field-group ${fieldErrors.keyfileName ? 'has-error' : ''}`}>
          <label className="configure-data-feed-modal__field-label">Keyfile</label>
          <div className="configure-data-feed-modal__file-row">
            <Button variant="outlined" component="label">
              Upload file
              <input hidden type="file" onChange={handleKeyfileChange} />
            </Button>
            <span className="configure-data-feed-modal__file-name">
              {formValues.keyfileName || 'No file selected'}
            </span>
          </div>
        </div>
        <InputTextField
          title="Remote Directory"
          onChange={handleUpdate('remoteDirectory')}
          placeholder="Enter remote directory"
          value={formValues.remoteDirectory}
          style={getInputStyle('remoteDirectory')}
        />
        <InputTextField
          title="Archive Directory"
          onChange={handleUpdate('archiveDirectory')}
          placeholder="Enter archive directory"
          value={formValues.archiveDirectory}
          style={getInputStyle('archiveDirectory')}
        />
        <InputTextField
          title="Filename Pattern"
          onChange={handleUpdate('filenamePattern')}
          placeholder="Enter filename pattern"
          value={formValues.filenamePattern}
          style={getInputStyle('filenamePattern')}
        />

        <div className={`configure-data-feed-modal__field-group ${fieldErrors.frequency ? 'has-error' : ''}`}>
          <label className="configure-data-feed-modal__field-label">Frequency</label>
          <div className="configure-data-feed-modal__radio-group">
            {['hourly', 'daily', 'weekly'].map((option) => (
              <label key={option} className="configure-data-feed-modal__radio-option">
                <input
                  type="radio"
                  name="frequency"
                  value={option}
                  checked={formValues.frequency === option}
                  onChange={(event) => handleUpdate('frequency')(event.target.value)}
                />
                {option.charAt(0).toUpperCase() + option.slice(1)}
              </label>
            ))}
          </div>
        </div>

        <div className="configure-data-feed-modal__actions">
          <Button variant="contained" color="primary" onClick={handleSave}>
            {isEdit ? 'Update configuration' : 'Save configuration'}
          </Button>
        </div>
      </div>
    </FullModal>
  );
};

export default ConfigureDataFeedModal;
